import React, { ButtonHTMLAttributes, forwardRef, useRef } from 'react';
import { gsap } from 'gsap';

interface AuthButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary';
  isLoading?: boolean;
  loadingText?: string;
}

const AuthButton = forwardRef<HTMLButtonElement, AuthButtonProps>(
  (
    {
      children,
      variant = 'primary',
      isLoading = false,
      loadingText = 'Loading...',
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const sparkleRefs = useRef<HTMLSpanElement[]>([]);

    // Handle hover effect
    const handleMouseEnter = () => {
      if (!disabled && !isLoading && buttonRef.current) {
        gsap.to(buttonRef.current, {
          scale: 1.02,
          boxShadow: '0 0 30px rgba(14, 165, 233, 0.4)',
          duration: 0.3,
          ease: 'power2.out',
        });

        // Animate sparkles
        sparkleRefs.current.forEach((sparkle, i) => {
          gsap.fromTo(
            sparkle,
            { opacity: 0, scale: 0 },
            {
              opacity: 1,
              scale: 1,
              duration: 0.4,
              delay: i * 0.1,
              ease: 'back.out(1.7)',
            }
          );
        });
      }
    };

    const handleMouseLeave = () => {
      if (!disabled && !isLoading && buttonRef.current) {
        gsap.to(buttonRef.current, {
          scale: 1,
          boxShadow: '0 0 20px rgba(14, 165, 233, 0.2)',
          duration: 0.3,
          ease: 'power2.out',
        });
      }
    };

    const baseStyles = `
      relative w-full py-3 px-6
      font-medium text-white
      rounded-lg
      transition-all duration-300
      disabled:opacity-50 disabled:cursor-not-allowed
      overflow-hidden
    `;

    const variantStyles = {
      primary: `
        bg-gradient-to-r from-cyan-500 to-purple-600
        hover:from-cyan-400 hover:to-purple-500
        shadow-[0_0_20px_rgba(14,165,233,0.2)]
        hover:shadow-[0_0_30px_rgba(14,165,233,0.4)]
      `,
      secondary: `
        bg-[rgba(30,41,59,0.6)]
        backdrop-blur-xl
        border border-[rgba(148,163,184,0.2)]
        hover:border-cyan-500/50
        hover:bg-[rgba(30,41,59,0.8)]
      `,
    };

    return (
      <button
        ref={node => {
          // Handle both refs
          if (node) {
            buttonRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }
        }}
        className={`${baseStyles} ${variantStyles[variant]} ${className}`}
        disabled={disabled || isLoading}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {/* Shimmer effect */}
        <div className='absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent' />

        {/* Sparkle effects */}
        {!isLoading && !disabled && variant === 'primary' && (
          <>
            <span
              ref={el => {
                if (el) sparkleRefs.current.push(el);
              }}
              className='absolute top-1 left-2 w-1 h-1 bg-white rounded-full opacity-0'
            />
            <span
              ref={el => {
                if (el) sparkleRefs.current.push(el);
              }}
              className='absolute top-2 right-3 w-1.5 h-1.5 bg-white rounded-full opacity-0'
            />
            <span
              ref={el => {
                if (el) sparkleRefs.current.push(el);
              }}
              className='absolute bottom-1 right-6 w-1 h-1 bg-white rounded-full opacity-0'
            />
          </>
        )}

        {isLoading ? (
          <span className='flex items-center justify-center gap-2'>
            <svg
              className='animate-spin h-5 w-5'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
            {loadingText}
          </span>
        ) : (
          children
        )}
      </button>
    );
  }
);

AuthButton.displayName = 'AuthButton';

export default AuthButton;
